/**
 * How an attendance status reads, in one place.
 *
 * Five screens render one of these and three of them were printing
 * `status.replace(/_/g, ' ')` — so the same day read "Half day" on the records
 * table and "half day" on My Day, and "weekly off" where the table said
 * "Weekly off". The two that were right each carried their own map, which is
 * how the other three came to differ: there was nothing to be wrong against.
 *
 * One map covering both enums. `AttendanceStatus` is what a scored day holds
 * and `AttendanceRecordStatus` is the roster's wider set — it adds the four
 * states that exist when there is no day to score at all (not yet started, not
 * due yet, no shift assigned, and the rail of `late` that the rollup separates
 * out). They overlap on six values and never disagree about one, so a single
 * map keyed by the string serves both rather than two that must be kept in
 * step by hand.
 *
 * Tones deliberately stay with the components. Colour is a decision about a
 * particular table — the records view greys "the day was never theirs" and
 * ambers "somebody should look" — and it is not the same decision on a
 * personal timeline as on a monitor.
 */

/** Every value either enum can hold, as a person reads it. */
export const ATTENDANCE_STATUS_LABELS: Record<string, string> = {
  present: 'Present',
  late: 'Late',
  half_day: 'Half day',
  absent: 'Absent',
  leave: 'On leave',
  holiday: 'Holiday',
  weekly_off: 'Weekly off',
  unmonitored: 'Unmonitored',
  not_yet_started: 'Not yet started',
  not_due_yet: 'Not due yet',
  no_shift_assigned: 'No shift assigned',
};

/**
 * The label, or a readable fallback.
 *
 * Falls back to the raw spelling rather than to a blank: if either enum grows
 * before this map does, an unlabelled value on screen is how somebody
 * notices. Null in, null out, so a cell keeps its own em dash rather than
 * being handed the string "null".
 */
export function attendanceStatusLabel(
  status: string | null | undefined,
): string | null {
  if (!status) return null;
  return ATTENDANCE_STATUS_LABELS[status] ?? status.replace(/_/g, ' ');
}
